import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router";
import { PageHeader } from "@/components/PageHeader";
import { SearchInput } from "@/components/SearchInput";
import { Button } from "@/components/ui/button";
import { DocumentTable } from "@/features/documents/DocumentTable";
import { NewDocumentDialog } from "@/features/documents/NewDocumentDialog";
import { Pipeline } from "@/features/documents/Pipeline";
import { ReadyList } from "@/features/documents/ReadyList";
import { get } from "@/lib/api";
import { ARTS, artFilter, listFooter, stageFilter, type Stage } from "@/lib/documents";
import type { DocumentRow, Pipeline as PipelineData, ReadyRow } from "@/lib/types";
import { cn } from "@/lib/utils";

const TITLES: Record<string, string> = { draft: "Entwürfe", open: "Offene Rechnungen", paid: "Bezahlte Rechnungen" };

function typingTarget(t: EventTarget | null): boolean {
  return t instanceof HTMLElement && (t.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(t.tagName));
}

export function Rechnungen() {
  const [sp, setSp] = useSearchParams();
  const art = artFilter(sp.get("art"));
  const stage = stageFilter(sp.get("stufe"));
  const q = sp.get("q") ?? "";
  const set = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(sp);
    for (const [k, v] of Object.entries(patch)) v ? next.set(k, v) : next.delete(k);
    setSp(next, { replace: true });
  };
  const [creating, setCreating] = useState(false);
  const type = art === "angebote" ? "offer" : "invoice";
  const canCreate = art !== "gutschriften";

  // The search runs on the server: wait for a pause in typing.
  const [dq, setDq] = useState(q);
  useEffect(() => {
    const t = setTimeout(() => setDq(q), 250);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    if (!canCreate) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() !== "n" || e.metaKey || e.ctrlKey || e.altKey || typingTarget(e.target) || document.querySelector('[role="dialog"]')) return;
      e.preventDefault();
      setCreating(true);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [canCreate]);

  const pipeline = useQuery({ queryKey: ["documents", "pipeline"], queryFn: () => get<PipelineData>("/api/documents/pipeline") });
  const ready = useQuery({ queryKey: ["documents", "ready"], queryFn: () => get<{ rows: ReadyRow[] }>("/api/documents/ready") });
  // Stage "ready" is a pipeline step with no documents behind it: it shows the ready list only.
  const apiStage = stage === "ready" ? null : stage;
  const list = useQuery({
    queryKey: ["documents", "list", art, apiStage, dq],
    queryFn: () => get<{ rows: DocumentRow[]; total: number }>(`/api/documents?${new URLSearchParams({ art, ...(apiStage ? { stufe: apiStage } : {}), ...(dq.trim() ? { q: dq.trim() } : {}) })}`),
    placeholderData: keepPreviousData,
  });

  const showReady = art === "rechnungen" && (stage === null || stage === "ready");
  const showTable = stage !== "ready";
  const rows = list.data?.rows ?? [];
  const noun = art === "angebote" ? "Angebote" : art === "gutschriften" ? "Gutschriften" : "Rechnungen";
  const title = apiStage ? (TITLES[apiStage] ?? `Alle ${noun}`) : `Alle ${noun}`;
  const empty = dq.trim() ? `Nichts zu „${dq.trim()}“.`
    : stage === "open" || stage === "paid" ? "Noch nichts vorhanden. Offene und bezahlte Rechnungen erscheinen, sobald die sevDesk-Dokumente importiert sind."
    : stage === "draft" ? "Keine Entwürfe."
    : `Keine ${noun}.`;

  return (
    <div className="space-y-5">
      <PageHeader
        title="Rechnungen"
        actions={canCreate && (
          <Button size="sm" className="pointer-coarse:h-11" onClick={() => setCreating(true)} title="Tastenkürzel N">
            <Plus />
            {type === "offer" ? "Neues Angebot" : "Neue Rechnung"}
            <kbd className="ml-0.5 rounded border border-primary-foreground/35 px-1.5 text-[0.6875rem] leading-4 opacity-80 pointer-coarse:hidden">N</kbd>
          </Button>
        )}
      />
      <div role="tablist" aria-label="Dokumentart" className="inline-flex gap-0.5 rounded-lg bg-muted p-[3px]">
        {ARTS.map((a) => (
          <button
            key={a.key}
            role="tab"
            type="button"
            aria-selected={art === a.key}
            onClick={() => set({ art: a.key === "rechnungen" ? null : a.key, stufe: null })}
            className={cn(
              "h-7 rounded-md px-3 text-[0.8125rem] outline-none transition-colors duration-150 focus-visible:ring-[3px] focus-visible:ring-ring/50 motion-reduce:transition-none pointer-coarse:h-11",
              art === a.key ? "bg-card font-medium text-foreground shadow-xs" : "text-muted-foreground hover:text-foreground",
            )}
          >
            {a.label}
          </button>
        ))}
      </div>

      {art === "rechnungen" && <Pipeline data={pipeline.data} stage={stage} onPick={(s: Stage | null) => set({ stufe: s })} />}
      {pipeline.isError && <p className="text-sm text-destructive">{pipeline.error.message}</p>}

      {showReady && (ready.isError ? <p className="text-sm text-destructive">{ready.error.message}</p> : <ReadyList rows={ready.data?.rows} />)}

      {showTable && (
        <section aria-labelledby="h-list" className="space-y-2.5">
          <div className="flex flex-wrap items-center gap-2">
            <h2 id="h-list" className="mr-2 text-base font-semibold">{title}</h2>
            <SearchInput className="w-full sm:ml-auto sm:max-w-80 sm:flex-1" value={q} onChange={(v) => set({ q: v })} placeholder="Nummer, Kunde, Position" />
          </div>
          {list.isError && <p className="text-sm text-destructive">{list.error.message}</p>}
          <div aria-busy={list.isPlaceholderData} className={cn("transition-opacity duration-150 motion-reduce:transition-none", list.isPlaceholderData && "opacity-60")}>
            <DocumentTable rows={rows} loading={list.isPending} empty={empty} />
          </div>
          {list.data && <p className="text-[0.8125rem] text-muted-foreground">{listFooter(rows.length, list.data.total, !!apiStage || !!dq.trim())}</p>}
        </section>
      )}

      <NewDocumentDialog key={type} type={type} open={creating} onClose={() => setCreating(false)} />
    </div>
  );
}
